import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useAuth } from '../auth';
import { useAdminOnlyPermissions } from '../Admin/useAdminOnlyPermissions';

/**
 * ProtectedRoute plus an Admin-role check, for pages that hiding a nav link is not
 * enough to protect. Any signed-in staff account could otherwise reach an admin page
 * by typing its URL, since ProtectedRoute only asks whether someone is logged in.
 *
 * The backend stays the source of truth (it rejects unauthorized calls); this keeps
 * the URL from being the whole of the front-end gate.
 */
function AdminRoute({ children }) {
    const { user, loading, expired } = useAuth();
    const { isAdmin, loading: permissionsLoading } = useAdminOnlyPermissions();
    const location = useLocation();

    if (loading || permissionsLoading) {
        return <div>Loading...</div>;
    }

    // Same as ProtectedRoute: the takeover holds this route open rather than unmounting the
    // page it just signed the manager out of. It also suspends the admin check, because the
    // permission lookup is keyed on the signed-in name and answers "not an admin" for nobody.
    const heldByTakeover = !user && expired;

    // Admin pages are a manager's: send them to the Google view, not the trainer PIN screen.
    if (!user && !heldByTakeover) {
        return <Navigate to="/login/google" state={{ from: location }} replace />;
    }

    // Home rather than /login: they are signed in, just not an admin, and bouncing
    // them to a login form they have already passed reads as a broken app.
    if (!isAdmin && !heldByTakeover) {
        return <Navigate to="/" replace />;
    }

    return children;
}

AdminRoute.propTypes = {
    children: PropTypes.node.isRequired,
};

export default AdminRoute;
